import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * The dashboard documents detail screen's `vw_file_shares` row — the
 * client-level (`user_id IS NULL`) share the screen resolves for one
 * `(file_id, client_id)` pair, mirroring `online-course-shares.
 * getOnlineCourseShare`'s own-or-client-level resolution for the sibling
 * online-courses detail screen. `vw_file_shares` carries no `url`/
 * `is_mandatory`/`is_initial_audit`/`status` columns (those exist only on
 * `vw_online_course_shares`) — the shared `AssetDetailPage` component
 * defaults them client-side for the document case.
 */
export const FileShareDetail = S.Struct({
    id: S.UUID,
    file_id: S.UUID,
    translation_group_id: S.NullOr(S.Int),
    client_id: S.NullOr(S.UUID),
    user_id: S.NullOr(S.UUID),
    filename_download: S.NullOr(S.String),
    description: S.NullOr(S.String),
    folder: S.NullOr(S.UUID),
    folder_parent: S.NullOr(S.UUID),
    folder_share_id: S.NullOr(S.UUID),
    completed: S.NullOr(S.Boolean),
    completed_at: S.NullOr(S.String),
    metadata: S.NullOr(S.Unknown),
    uploaded_by: S.NullOr(S.UUID),
    visibility_scope: S.NullOr(S.String),
    share_date: S.NullOr(S.String),
    shared_by_first_name: S.NullOr(S.String),
    shared_by_last_name: S.NullOr(S.String),
});
export const FileShareNotFound = S.Struct({
    _tag: S.Literal("FileShareNotFound"),
});
export const GetFileShareParams = S.Struct({
    file_id: S.UUID,
    client_id: S.UUID,
    user_id: S.optional(S.UUID),
});
/**
 * `user_id` omitted resolves the client-level share — the admin/ohs-officer/
 * location-manager documents detail view; `user_id` present resolves the
 * caller's own per-user share. The caller may only ever pass their own id.
 */
export const GetFileShare = HttpApiEndpoint.get("getFileShare", "/dashboard/documents/file-share")
    .setUrlParams(GetFileShareParams)
    .addSuccess(S.Struct({ data: FileShareDetail }))
    .addError(FileShareNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
const UuidCsvList = S.transform(S.String, S.Array(S.UUID), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    encode: (ids) => ids.join(","),
});
/**
 * The shared `AssetDetailPage` component's two tab counts — "assigned"
 * (open `vw_measures` rows for this item's translation group, either
 * document- or online-course-typed) and "shared" (per-user shares scoped to
 * one client/language), folding the legacy screen's two sequential
 * aggregate fetches into one round trip. Generic across both asset types,
 * unlike `online-course-shares.getOnlineCourseMetrics` (online-courses-card
 * specific three-count shape).
 */
export const AssetShareCounts = S.Struct({
    assignedCount: S.Int,
    sharedCount: S.Int,
});
export const AssetShareType = S.Literal("document", "online_course");
export const GetAssetShareCountsParams = S.Struct({
    type: AssetShareType,
    translation_group_id: S.NumberFromString.pipe(S.int()),
    client_id: S.UUID,
    language: LanguageSchema,
    location_ids: S.optional(UuidCsvList),
});
export const GetAssetShareCounts = HttpApiEndpoint.get("getAssetShareCounts", "/dashboard/documents/share-counts")
    .setUrlParams(GetAssetShareCountsParams)
    .addSuccess(S.Struct({ data: AssetShareCounts }))
    .addError(Forbidden, { status: 403 });
export const AssetRecipientRow = S.Struct({
    kind: S.Literal("recipient"),
    id: S.UUID,
    user_id: S.UUID,
    user_first_name: S.NullOr(S.String),
    user_last_name: S.NullOr(S.String),
    user_email: S.NullOr(S.String),
    share_date: S.NullOr(S.String),
    location_ids: S.NullOr(S.String),
});
export const ListAssetRecipientsParams = S.extend(PagingParams, S.Struct({
    type: AssetShareType,
    translation_group_id: S.NumberFromString.pipe(S.int()),
    client_id: S.UUID,
    language: LanguageSchema,
    location_ids: S.optional(UuidCsvList),
}));
export const ListAssetRecipients = HttpApiEndpoint.get("listAssetRecipients", "/dashboard/documents/recipients")
    .setUrlParams(ListAssetRecipientsParams)
    .addSuccess(pagedResponse(AssetRecipientRow))
    .addError(Forbidden, { status: 403 });
export const dashboardFileSharesGroup = HttpApiGroup.make("dashboard-file-shares")
    .add(GetFileShare)
    .add(GetAssetShareCounts)
    .add(ListAssetRecipients)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=dashboard-file-shares.js.map